import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

type Props = {
  color: string;
  onChange: (hex: string) => void;
  /** Вызывается по окончании выбора (после drag / отпускания слайдера) — для сохранения в swatch. */
  onCommit?: (hex: string) => void;
  className?: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** RGB 0–255 → HSV h∈[0,360), s∈[0,1], v∈[0,1] */
function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
    else if (max === gn) h = ((bn - rn) / d + 2) * 60;
    else h = ((rn - gn) / d + 4) * 60;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return {
    r: (rp + m) * 255,
    g: (gp + m) * 255,
    b: (bp + m) * 255,
  };
}

function hueCss(h: number): string {
  const { r, g, b } = hsvToRgb(h, 1, 1);
  return rgbToHex(r, g, b);
}

/**
 * Палитра в стиле Miro: поле насыщенность/яркость + слайдер оттенка.
 */
export function BoardHuePicker({ color, onChange, onCommit, className = "" }: Props) {
  const rgb = hexToRgb(color) ?? { r: 26, g: 26, b: 46 };
  const initial = rgbToHsv(rgb.r, rgb.g, rgb.b);
  const [hue, setHue] = useState(initial.h);
  const [sat, setSat] = useState(initial.s);
  const [val, setVal] = useState(initial.v);
  const lastExternal = useRef(color.toLowerCase());
  const lastEmitted = useRef(color.toLowerCase());
  const svRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    const next = color.toLowerCase();
    if (next === lastExternal.current) return;
    lastExternal.current = next;
    const parsed = hexToRgb(color);
    if (!parsed) return;
    const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
    setHue(hsv.h);
    setSat(hsv.s);
    setVal(hsv.v);
  }, [color]);

  const emit = useCallback(
    (h: number, s: number, v: number) => {
      const { r, g, b } = hsvToRgb(h, s, v);
      const hex = rgbToHex(r, g, b);
      lastExternal.current = hex;
      lastEmitted.current = hex;
      onChange(hex);
    },
    [onChange]
  );

  const commit = useCallback(() => {
    onCommitRef.current?.(lastEmitted.current);
  }, []);

  const setSvFromPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = svRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const s = clamp((clientX - rect.left) / rect.width, 0, 1);
      const v = clamp(1 - (clientY - rect.top) / rect.height, 0, 1);
      setSat(s);
      setVal(v);
      emit(hue, s, v);
    },
    [emit, hue]
  );

  useLayoutEffect(() => {
    window.dispatchEvent(new Event("wb:tool-options-layout"));
  }, []);

  useEffect(() => {
    function onMove(ev: PointerEvent) {
      if (!dragging.current) return;
      setSvFromPointer(ev.clientX, ev.clientY);
    }
    function onUp() {
      if (!dragging.current) return;
      dragging.current = false;
      commit();
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [setSvFromPointer, commit]);

  return (
    <div className={`wb-hue-picker ${className}`.trim()} role="group" aria-label="Свой цвет">
      <div
        ref={svRef}
        className="wb-hue-picker__sv"
        style={{ backgroundColor: hueCss(hue) }}
        onPointerDown={(ev) => {
          dragging.current = true;
          (ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId);
          setSvFromPointer(ev.clientX, ev.clientY);
        }}
      >
        <div className="wb-hue-picker__sv-white" />
        <div className="wb-hue-picker__sv-black" />
        <span
          className="wb-hue-picker__thumb"
          style={{ left: `${sat * 100}%`, top: `${(1 - val) * 100}%` }}
        />
      </div>
      <label className="wb-hue-picker__hue-label">
        <span className="wb-visually-hidden">Оттенок</span>
        <input
          type="range"
          className="wb-hue-picker__hue"
          min={0}
          max={360}
          step={1}
          value={Math.round(hue)}
          aria-label="Оттенок"
          onChange={(ev) => {
            const h = Number(ev.target.value);
            setHue(h);
            emit(h, sat, val);
          }}
          onPointerUp={() => commit()}
          onKeyUp={() => commit()}
        />
      </label>
      <div className="wb-hue-picker__preview" style={{ background: color }} title={color} />
    </div>
  );
}
